import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '@/store'

// Define API response types
export interface AuthResponse {
  access_token: string
  token_type: string
  expires_in: number
}

export interface InterviewSession {
  id: string
  candidate_id: string
  organization_id: string
  candidate_name?: string
  position?: string
  employment_type?: string
  status: string
  current_competency: string
  questions_asked: number
  competency_questions_limit: number
  start_time?: string
  end_time?: string
  time_remaining?: number
  interview_duration?: number
  interview_ended_by_ai: boolean
  ai_end_reason?: string
  ai_final_message?: string
  created_at: string
  updated_at: string
}

export interface InterviewMessage {
  id: string
  session_id: string
  content: string
  is_ai: boolean
  competency?: string
  created_at: string
}

export interface InterviewProgress {
  progress: number
  current_stage: string
  questions_asked: number
  total_questions: number
  current_competency: string
  completed_competencies: string[]
}

export interface TranscriptionResponse {
  text?: string // Old format
  transcription?: string // New format
  confidence?: number
  processing_time?: number
  // Additional fields from new response format
  success?: boolean
  segments?: Array<{
    start: number
    end: number
    text: string
  }>
  metadata?: {
    filename: string
    file_size: number
    file_size_mb: number
    language: string
    language_probability: number
    duration: number
    beam_size: number
    batch_size: number
    temperature: number
    segment_count: number
    processing_time_seconds: number
    transcription_time_seconds: number
    rtf: number
  }
}

export interface CreateSessionRequest {
  candidate_id: string
  organization_id: string
  candidate_name?: string
  position?: string
  employment_type?: string
  interview_duration?: number
  competency_questions_limit?: number
  competency_config?: string[]
  psychometric_result_url?: string
  company_profile_url?: string
}

export interface TokenRequest {
  candidate_id: string
  organization: string
  candidate_name?: string
  position?: string
}

export interface TranscriptionRequest {
  audio_data: string
  filename?: string
  session_id?: string
}

export interface NoiseConsentRequest {
  noise_level: 'low' | 'moderate' | 'high'
  recommendation: string
  accepted: boolean
}

export interface NoiseConsentResponse {
  success: boolean
  message: string
  session_id: string
  consent_timestamp: string
}

// Create the API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1',
    prepareHeaders: (headers, { getState }) => {
      // Get token from auth state
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Session', 'Message', 'Progress'],
  endpoints: (builder) => ({
    // Authentication
    createToken: builder.mutation<AuthResponse, TokenRequest>({
      query: (credentials) => ({
        url: '/auth/token',
        method: 'POST',
        body: credentials,
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_API_KEY}`,
        },
      }),
    }),

    // Interview Sessions
    createSession: builder.mutation<InterviewSession, CreateSessionRequest>({
      query: (sessionData) => ({
        url: '/interview/sessions',
        method: 'POST',
        body: sessionData,
      }),
      invalidatesTags: ['Session'],
    }),

    getSession: builder.query<InterviewSession, string>({
      query: (sessionId) => `/interview/sessions/${sessionId}`,
      providesTags: (_, __, sessionId) => [{ type: 'Session', id: sessionId }],
    }),

    endSession: builder.mutation<{ message: string; session_id: string }, string>({
      query: (sessionId) => ({
        url: `/interview/sessions/${sessionId}/end`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, sessionId) => [{ type: 'Session', id: sessionId }],
    }),

    // Messages
    getSessionMessages: builder.query<InterviewMessage[], string>({
      query: (sessionId) => `/interview/sessions/${sessionId}/messages`,
      providesTags: (result) => [
        { type: 'Message', id: 'LIST' },
        ...(result?.map(({ id }) => ({ type: 'Message' as const, id })) ?? []),
      ],
    }),

    addMessage: builder.mutation<InterviewMessage, { sessionId: string; message: Omit<InterviewMessage, 'id' | 'session_id' | 'created_at'> }>({
      query: ({ sessionId, message }) => ({
        url: `/interview/sessions/${sessionId}/messages`,
        method: 'POST',
        body: message,
      }),
      invalidatesTags: [{ type: 'Message', id: 'LIST' }],
    }),

    // Progress
    getProgress: builder.query<InterviewProgress, string>({
      query: (sessionId) => `/interview/sessions/${sessionId}/progress`,
      providesTags: (_, __, sessionId) => [{ type: 'Progress', id: sessionId }],
    }),

    // Transcription
    transcribeAudio: builder.mutation<TranscriptionResponse, TranscriptionRequest>({
      query: (data) => ({
        url: '/interview/transcribe',
        method: 'POST',
        body: data,
      }),
    }),

    // Noise consent
    logNoiseConsent: builder.mutation<NoiseConsentResponse, { sessionId: string; consent: NoiseConsentRequest }>({
      query: ({ sessionId, consent }) => ({
        url: `/interview/sessions/${sessionId}/noise-consent`,
        method: 'POST',
        body: consent,
      }),
    }),
  }),
})

// Export hooks for usage in components
export const {
  useCreateTokenMutation,
  useCreateSessionMutation,
  useGetSessionQuery,
  useEndSessionMutation,
  useGetSessionMessagesQuery,
  useAddMessageMutation,
  useGetProgressQuery,
  useTranscribeAudioMutation,
  useLogNoiseConsentMutation,
} = apiSlice